/**
 * El objeto de dibujo de una persona.
 *
 * Debajo del nombre va UNA sola cosa: el parentesco respecto al punto de
 * perspectiva que se haya elegido ("padre", "prima hermana", "medio tio"). Si
 * no hay perspectiva elegida, o si con esa persona no hay parentesco, no va
 * nada.
 *
 * Antes ahi se amontonaban los dos apellidos con su origen, las fechas, el
 * lugar y la generacion de linaje, y era demasiada informacion: el diagrama
 * se volvia ilegible justo al acercarse a leerlo. Todo ese detalle sigue
 * estando, pero en la ficha lateral, que es donde se lee bien.
 *
 * Como se evita que las etiquetas se pisen: el texto de cada persona esta
 * limitado al ancho de su propia tarjeta (con corte de linea por palabras) y
 * la disposicion garantiza que dos personas de la misma fila estan a
 * `SEP_MINIMA` una de otra, que es mayor que ese ancho. Asi dos etiquetas no
 * pueden solaparse nunca, por construccion, sin tener que ir apartandolas en
 * cada cuadro.
 *
 * Los objetos de texto se crean cuando hacen falta por primera vez y se
 * guardan: crearlos y destruirlos al mover el zoom provocaria tirones.
 */
import { Container, Sprite, Text, type Texture } from "pixi.js";
import { ALTO_FILA, ALTO_NODO, ANCHO_NODO } from "../layout/medidas.js";
import { apellidoPrimario, nombrePrincipal, type Persona } from "../core/model.js";
import { escalaPunto, type Detalle } from "./lod.js";
import { colorDeApellido, estiloDe, FUENTE, RELACION } from "./tema.js";
import type { Texturas } from "./texturas.js";

const ANCHO_TEXTO = ANCHO_NODO - 18;

export class NodoVista {
  readonly raiz = new Container();
  private readonly resplandor: Sprite;
  private readonly punto: Sprite;
  private readonly tarjeta: Sprite;
  private readonly marco: Sprite;
  private nombre?: Text;
  private relacion?: Text;

  /** Desfase de la respiracion, para que no respiren todos al unisono. */
  readonly fase: number;
  /** Opacidad objetivo del resaltado; se persigue con suavizado. */
  alfaObjetivo = 1;
  /** Opacidad aplicada. */
  alfaActual = 1;

  private readonly textoNombre: string;
  /**
   * Parentesco respecto al punto de perspectiva. Cadena vacia = no se muestra
   * nada, que es lo que toca cuando no hay perspectiva elegida o cuando con
   * esa persona no hay ningun parentesco.
   */
  private textoRelacion = "";
  private readonly ladoPunto: number;

  constructor(
    readonly handle: string,
    readonly persona: Persona,
    tex: Texturas,
  ) {
    const estilo = estiloDe(persona.sexo);
    this.fase = hash01(handle) * Math.PI * 2;

    this.resplandor = new Sprite(tex.resplandor);
    this.resplandor.anchor.set(0.5);
    this.resplandor.tint = estilo.resplandor;
    // El halo tiene que caber holgadamente dentro de la separacion entre
    // personas (SEP_MINIMA). Si se pasa, los resplandores de la fila se
    // funden en una mancha y se pierde la estructura, que es justo lo que hay
    // que ver cuando se esta alejado.
    this.resplandor.width = ANCHO_NODO * 1.08;
    this.resplandor.height = ALTO_NODO * 2.0;
    this.resplandor.alpha = 0.4;

    const texPunto = tex.punto[persona.sexo] ?? tex.punto["U"];
    this.punto = new Sprite(texPunto);
    this.punto.anchor.set(0.5);
    this.ladoPunto = texPunto?.width ?? 48;
    // El color del punto va por apellido, no por sexo: cuando se esta muy
    // alejado y no hay texto, eso es lo que deja ver las ramas de un vistazo.
    // La FORMA sigue diciendo el sexo.
    const ap = apellidoPrimario(nombrePrincipal(persona))?.texto ?? "";
    this.punto.tint = colorDeApellido(ap);

    const texTarjeta = tex.tarjeta[persona.sexo] ?? tex.tarjeta["U"];
    this.tarjeta = new Sprite(texTarjeta);
    this.tarjeta.anchor.set(0.5);
    this.tarjeta.width = ANCHO_NODO;
    this.tarjeta.height = ALTO_NODO;

    this.marco = new Sprite(tex.seleccion);
    this.marco.anchor.set(0.5);
    this.marco.width = ANCHO_NODO + 10;
    this.marco.height = ALTO_NODO + 10;
    this.marco.tint = 0xffd98a;
    this.marco.visible = false;

    this.raiz.addChild(this.resplandor, this.punto, this.tarjeta, this.marco);
    this.raiz.eventMode = "none";

    this.textoNombre = componerNombre(persona);
  }

  /**
   * Pone (o quita) la etiqueta de parentesco que va bajo el nombre.
   * Cadena vacia la quita.
   */
  ponerRelacion(texto: string): void {
    if (texto === this.textoRelacion) return;
    this.textoRelacion = texto;
    if (this.relacion) {
      this.relacion.text = texto;
      this.relacion.visible = texto !== "";
    }
  }

  /** Coloca el nodo en el mundo. */
  mover(x: number, y: number): void {
    this.raiz.position.set(x, y);
  }

  get x(): number {
    return this.raiz.position.x;
  }

  get y(): number {
    return this.raiz.position.y;
  }

  seleccionar(si: boolean): void {
    this.marco.visible = si;
  }

  /**
   * Aplica el nivel de detalle y la respiracion.
   *
   * `respiracion` es un valor en torno a 0 que modula el resplandor. Es muy
   * leve a proposito: tiene que dar sensacion de vida sin distraer.
   */
  aplicar(d: Detalle, escalaCamara: number, respiracion: number): void {
    const a = this.alfaActual;

    this.tarjeta.alpha = d.tarjeta * a;
    this.tarjeta.visible = d.tarjeta > 0.004;

    this.punto.alpha = d.punto * a;
    this.punto.visible = d.punto > 0.004;
    if (this.punto.visible) {
      const e = escalaPunto(escalaCamara, this.ladoPunto);
      this.punto.scale.set(e);
    }

    this.resplandor.alpha = (0.1 + 0.13 * d.tarjeta + respiracion) * a;

    if (d.necesitaNombre) {
      if (!this.nombre) this.nombre = this.crearNombre();
      this.nombre.visible = true;
      this.nombre.alpha = d.nombre * a;
    } else if (this.nombre) {
      this.nombre.visible = false;
    }

    if (d.necesitaRelacion && this.textoRelacion) {
      if (!this.relacion) this.relacion = this.crearRelacion();
      this.relacion.visible = true;
      this.relacion.alpha = d.relacion * a;
    } else if (this.relacion) {
      this.relacion.visible = false;
    }

    if (this.marco.visible) this.marco.alpha = a;
  }

  private crearNombre(): Text {
    const estilo = estiloDe(this.persona.sexo);
    const t = new Text({
      text: this.textoNombre,
      style: {
        fontFamily: FUENTE,
        fontSize: 15,
        fontWeight: "600",
        fill: estilo.texto,
        align: "center",
        wordWrap: true,
        wordWrapWidth: ANCHO_TEXTO,
        lineHeight: 17,
      },
    });
    t.resolution = 2;
    t.anchor.set(0.5);
    t.eventMode = "none";
    this.raiz.addChild(t);
    return t;
  }

  private crearRelacion(): Text {
    const t = new Text({
      text: this.textoRelacion,
      style: {
        fontFamily: FUENTE,
        fontSize: 13,
        fontStyle: "italic",
        fill: RELACION,
        align: "center",
        wordWrap: true,
        wordWrapWidth: ANCHO_TEXTO + 20,
        lineHeight: 15,
      },
    });
    t.resolution = 2;
    t.anchor.set(0.5, 0);
    // Cuelga por debajo de la tarjeta. Cabe de sobra: hasta la fila siguiente
    // hay ALTO_FILA y la tarjeta solo ocupa ALTO_NODO.
    t.position.set(0, ALTO_NODO / 2 + 6);
    t.eventMode = "none";
    this.raiz.addChild(t);
    return t;
  }

  /** Lo que ocupa la etiqueta de parentesco por debajo del centro del nodo. */
  altoRelacion(): number {
    return this.relacion?.visible ? this.relacion.height + ALTO_NODO / 2 + 6 : 0;
  }

  /** Hueco libre entre una fila y la siguiente. */
  static holguraVertical(): number {
    return ALTO_FILA - ALTO_NODO;
  }

  destruir(): void {
    this.raiz.destroy({ children: true });
  }
}

// ---------------------------------------------------------------------------

function componerNombre(p: Persona): string {
  const n = nombrePrincipal(p);
  const pila = n?.first?.trim() ?? "";
  const aps = (n?.apellidos ?? [])
    .map((a) => a.texto.trim())
    .filter(Boolean)
    .join(" ");
  // Una persona puede no tener nombre de pila, o no tener apellidos. Las dos
  // cosas son validas y salen en el archivo real.
  return [pila, aps].filter(Boolean).join("\n") || (p.id ?? "");
}

/** Hash estable en [0,1), para dar a cada nodo su propia fase. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}
